import React, { useState, useRef, useEffect } from 'react';

interface MonthPickerProps {
    value: string; // YYYY-MM
    onChange: (value: string) => void;
}

const MONTHS = [
    'jan.', 'fev.', 'mar.', 'abr.', 'mai.', 'jun.',
    'jul.', 'ago.', 'set.', 'out.', 'nov.', 'dez.'
];

export const MonthPicker: React.FC<MonthPickerProps> = ({ value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const [year, month] = value.split('-').map(Number);
    const [viewYear, setViewYear] = useState(year);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleMonthClick = (mIndex: number) => {
        const newMonth = (mIndex + 1).toString().padStart(2, '0');
        onChange(`${viewYear}-${newMonth}`);
        setIsOpen(false);
    };

    const formattedLabel = month === 0 ? `Ano de ${year}` : `${MONTHS[month - 1]} de ${year}`;

    return (
        <div className="relative" ref={containerRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 bg-slate-800/40 border border-slate-700/50 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-300 hover:border-slate-500 hover:bg-slate-700/50 transition-all outline-none min-w-[180px]"
            >
                <span className="material-symbols-outlined text-slate-500 text-[18px]">calendar_month</span>
                <span className="flex-1 text-left uppercase tracking-wide text-xs font-bold">{formattedLabel}</span>
                <span className={`material-symbols-outlined text-slate-500 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>expand_more</span>
            </button>

            {isOpen && (
                <div
                    className="absolute top-full left-0 mt-2 z-50 w-72 border border-slate-700/60 rounded-2xl shadow-2xl p-4 animate-fade-in-up bg-slate-900"
                >
                    {/* Header do Ano */}
                    <div className="flex items-center justify-between mb-4 px-2">
                        <button
                            onClick={() => setViewYear(v => v - 1)}
                            className="p-1 hover:bg-slate-700/50 rounded-lg text-slate-400 hover:text-white transition-all shadow-lg"
                        >
                            <span className="material-symbols-outlined">chevron_left</span>
                        </button>
                        <div className="flex flex-col items-center">
                            <span className="text-sm font-black text-white tracking-widest">{viewYear}</span>
                            <button 
                                onClick={() => {
                                    onChange(`${viewYear}-00`);
                                    setIsOpen(false);
                                }}
                                className={`text-[9px] font-bold uppercase tracking-tighter mt-1 px-2 py-0.5 rounded ${year === viewYear && month === 0 ? 'bg-primary text-white' : 'text-primary hover:bg-primary/10'}`}
                            >
                                Consolidado Anual
                            </button>
                        </div>
                        <button
                            onClick={() => setViewYear(v => v + 1)}
                            className="p-1 hover:bg-slate-700/50 rounded-lg text-slate-400 hover:text-white transition-all shadow-lg"
                        >
                            <span className="material-symbols-outlined">chevron_right</span>
                        </button>
                    </div>

                    {/* Grid de Meses */}
                    <div className="grid grid-cols-3 gap-2">
                        {MONTHS.map((m, idx) => {
                            const isSelected = year === viewYear && (idx + 1) === month;
                            const isCurrentMonth = new Date().getFullYear() === viewYear && new Date().getMonth() === idx;

                            return (
                                <button
                                    key={m}
                                    onClick={() => handleMonthClick(idx)}
                                    className={`
                    py-3 px-2 rounded-xl text-[11px] font-bold uppercase tracking-wider transition-all
                    ${isSelected
                                             ? 'bg-primary text-white shadow-lg shadow-primary/30 scale-105'
                                             : 'text-slate-400 hover:bg-slate-700/40 hover:text-white'
                                         }
                    ${isCurrentMonth && !isSelected ? 'border border-primary/30' : ''}
                  `}
                                >
                                    {m}
                                </button>
                            );
                        })}
                    </div>

                    {/* Shortcuts */}
                    <div className="mt-4 pt-3 border-t border-slate-700/50 flex justify-between px-1">
                        <button
                            onClick={() => {
                                const now = new Date();
                                onChange(`${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`);
                                setIsOpen(false);
                            }}
                            className="text-[10px] font-bold text-primary hover:text-primary/80 uppercase tracking-widest transition-colors"
                        >
                            Este mês
                        </button>
                        <button
                            onClick={() => setIsOpen(false)}
                            className="text-[10px] font-bold text-slate-500 hover:text-slate-300 uppercase tracking-widest transition-colors"
                        >
                            Fechar
                        </button>
                    </div>

                </div>
            )}
        </div>
    );
};
